export const RecaptchaResultEnum = {
    VALID: 'valid',
    CHALLENGE: 'challenge',
    REJECT: 'reject',
} as const;

export type RecaptchaResult = typeof RecaptchaResultEnum[keyof typeof RecaptchaResultEnum];

export interface RecaptchaState {
    result: RecaptchaResult | null;
    score: number | null;
}

const STORAGE_KEY = '_pw_q1';
const TTL_MS = 10 * 60 * 1000;

interface StoredPayload {
    r: RecaptchaResult | null;
    s: number | null;
    t: number;
}

const storage: Storage | null = (() => {
    try {
        const s = window.sessionStorage;
        const probe = '__pw_probe__';
        s.setItem(probe, '1');
        s.removeItem(probe);
        return s;
    } catch {
        return null;
    }
})();

const readFromStorage = (): RecaptchaState => {
    if (!storage) {
        return { result: null, score: null };
    }
    try {
        const raw = storage.getItem(STORAGE_KEY);
        if (!raw) {
            return { result: null, score: null };
        }
        const payload = JSON.parse(atob(raw)) as StoredPayload;
        if (Date.now() - payload.t > TTL_MS) {
            storage.removeItem(STORAGE_KEY);
            return { result: null, score: null };
        }
        return { result: payload.r, score: payload.s };
    } catch {
        try {
            storage.removeItem(STORAGE_KEY);
        } catch {
            // noop
        }
        return { result: null, score: null };
    }
};

const writeToStorage = (s: RecaptchaState): void => {
    if (!storage) {
        return;
    }
    try {
        const payload: StoredPayload = { r: s.result, s: s.score, t: Date.now() };
        storage.setItem(STORAGE_KEY, btoa(JSON.stringify(payload)));
    } catch {
        // quota exceeded / disabled — keep in-memory state, ignore
    }
};

const state: RecaptchaState = { result: null, score: null };
const initial = readFromStorage();
state.result = initial.result;
state.score = initial.score;

document.addEventListener('recaptchaResultEvent', (e) => {
    const ev = e as CustomEvent<{ result?: RecaptchaResult | null; score?: number | null }>;
    state.result = ev.detail?.result ?? null;
    state.score = typeof ev.detail?.score === 'number' ? ev.detail.score : null;
    writeToStorage(state);
});

export const getRecaptchaState = (): RecaptchaState => {
    return { ...state };
};

export const getRecaptchaScore = (): number | null => {
    return state.score;
};

export const isRecaptchaRejected = (): boolean => {
    return state.result === RecaptchaResultEnum.REJECT;
};
